import React, { useState, useEffect } from 'react';
import { ListingSync } from '../components/ListingSync';
import { 
  Home, 
  Users, 
  MapPin, 
  Train, 
  DollarSign, 
  BedDouble, 
  Bath, 
  Calendar,
  Crown,
  Filter,
  Search,
  Map as MapIcon,
  List,
  Loader,
  RefreshCw
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { ListingsMap } from '../components/ListingsMap';
import { ListingInterestButton } from '../components/ListingInterestButton';
import { InterestedUsersModal } from '../components/InterestedUsersModal';
import type { Listing } from '../lib/types';

export function ListingsPage() {
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null);
  const [selectedListingForInterest, setSelectedListingForInterest] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    type: 'all',
    minPrice: 0,
    maxPrice: 10000,
    neighborhood: 'all',
    propertyType: 'all',
    source: 'all'
  });
  const [showFilters, setShowFilters] = useState(false);
  const [neighborhoods, setNeighborhoods] = useState<string[]>([]);

  useEffect(() => {
    loadListings();
    loadNeighborhoods();
  }, []);

  const loadListings = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('listings')
        .select(`
          *,
          locations (
            id,
            neighborhood,
            borough,
            zip_code
          ),
          listing_transit (
            id,
            distance_meters,
            walk_minutes,
            transit_stops (
              id,
              name,
              type,
              lines
            )
          )
        `)
        .eq('is_available', true)
        .order('source_priority', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform the data to match the expected format
      const transformedListings = data?.map(listing => ({
        ...listing,
        neighborhood: listing.locations?.neighborhood || 'Unknown',
        transit_stops: listing.listing_transit?.map(transit => ({
          name: transit.transit_stops.name,
          type: transit.transit_stops.type,
          lines: transit.transit_stops.lines,
          distance_meters: transit.distance_meters,
          walk_minutes: transit.walk_minutes
        })) || []
      }));

      setListings(transformedListings || []);
    } catch (error) {
      console.error('Error loading listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadNeighborhoods = async () => {
    try {
      const { data, error } = await supabase
        .from('locations')
        .select('neighborhood')
        .eq('borough', 'Brooklyn')
        .order('neighborhood');

      if (error) throw error;
      setNeighborhoods(Array.from(new Set(data.map(l => l.neighborhood))));
    } catch (error) {
      console.error('Error loading neighborhoods:', error);
    }
  };

  const syncListings = async () => {
    try {
      setSyncing(true);
      await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sync-listings`, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
      });
      await loadListings();
    } catch (error) {
      console.error('Error syncing listings:', error);
    } finally {
      setSyncing(false);
    }
  };

  const propertyTypes = [
    { value: 'all', label: 'All Types' },
    { value: 'studio', label: 'Studio' },
    { value: 'one_bedroom', label: 'One Bedroom' },
    { value: 'loft', label: 'Loft' },
    { value: 'micro_apartment', label: 'Micro Apartment' },
    { value: 'shared', label: 'Shared' }
  ];

  const sources = [
    { value: 'all', label: 'All Sources' },
    { value: 'listings_project', label: 'Listings Project' },
    { value: 'streeteasy', label: 'StreetEasy' }
  ];

  const filteredListings = listings.filter(listing => {
    if (filters.propertyType !== 'all' && listing.property_type !== filters.propertyType) return false;
    if (listing.price < filters.minPrice || listing.price > filters.maxPrice) return false;
    if (filters.neighborhood !== 'all' && listing.neighborhood !== filters.neighborhood) return false;
    if (filters.source !== 'all' && listing.source !== filters.source) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white py-8 px-4 flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white py-6 md:py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 md:mb-8">
          <ListingSync />
        </div>

        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 md:mb-8 gap-4">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Available Listings</h1>
            <button
              onClick={syncListings}
              disabled={syncing}
              className="flex items-center gap-2 px-3 py-2 md:px-4 md:py-2 bg-white rounded-lg shadow-sm hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 md:w-5 md:h-5 ${syncing ? 'animate-spin' : ''}`} />
              <span className="text-sm md:text-base">{syncing ? 'Syncing...' : 'Sync Now'}</span>
            </button>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setViewMode(viewMode === 'list' ? 'map' : 'list')}
              className="flex items-center gap-2 px-3 py-2 md:px-4 md:py-2 bg-white rounded-lg shadow-sm hover:bg-gray-50"
            >
              {viewMode === 'list' ? (
                <>
                  <MapIcon className="w-4 h-4 md:w-5 md:h-5" />
                  <span className="text-sm md:text-base">Map View</span>
                </>
              ) : (
                <>
                  <List className="w-4 h-4 md:w-5 md:h-5" />
                  <span className="text-sm md:text-base">List View</span>
                </>
              )}
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-3 py-2 md:px-4 md:py-2 bg-white rounded-lg shadow-sm hover:bg-gray-50"
            >
              <Filter className="w-4 h-4 md:w-5 md:h-5" />
              <span className="text-sm md:text-base">Filters</span>
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-6 md:mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Property Type
                </label>
                <select
                  value={filters.propertyType}
                  onChange={(e) => setFilters(prev => ({ ...prev, propertyType: e.target.value }))}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                >
                  {propertyTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Source
                </label>
                <select
                  value={filters.source}
                  onChange={(e) => setFilters(prev => ({ ...prev, source: e.target.value }))}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                >
                  {sources.map(source => (
                    <option key={source.value} value={source.value}>
                      {source.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Neighborhood
                </label>
                <select
                  value={filters.neighborhood}
                  onChange={(e) => setFilters(prev => ({ ...prev, neighborhood: e.target.value }))}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                >
                  <option value="all">All Neighborhoods</option>
                  {neighborhoods.map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price Range
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={filters.minPrice}
                    onChange={(e) => setFilters(prev => ({ ...prev, minPrice: parseInt(e.target.value) }))}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    placeholder="Min"
                  />
                  <span>-</span>
                  <input
                    type="number"
                    value={filters.maxPrice}
                    onChange={(e) => setFilters(prev => ({ ...prev, maxPrice: parseInt(e.target.value) }))}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    placeholder="Max"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {viewMode === 'map' ? (
          <div className="h-[70vh] md:h-[80vh] rounded-lg overflow-hidden">
            <ListingsMap 
              listings={filteredListings}
              selectedListing={selectedListing}
              onListingClick={setSelectedListing}
            />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4 md:gap-6">
            {filteredListings.map((listing) => (
              <div key={listing.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
                {listing.images[0] && (
                  <img 
                    src={listing.images[0]} 
                    alt={listing.title}
                    className="w-full h-40 md:h-48 object-cover"
                  />
                )}
                
                <div className="p-4 md:p-6">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4">
                    <div>
                      <h2 className="text-lg md:text-xl font-semibold text-gray-900">{listing.title}</h2>
                      <p className="text-gray-600 flex items-center mt-1">
                        <MapPin className="w-4 h-4 mr-1" />
                        {listing.neighborhood}
                      </p>
                    </div>
                    <div className="text-lg md:text-xl font-bold text-indigo-600 mt-2 md:mt-0">
                      ${listing.price.toLocaleString()}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 mb-4">
                    <span className="text-gray-600 text-sm md:text-base">{listing.bedrooms} bed</span>
                    <span className="text-gray-600 text-sm md:text-base">{listing.bathrooms} bath</span>
                    <span className="text-gray-600 text-sm md:text-base">
                      Available {new Date(listing.available_date).toLocaleDateString()}
                    </span>
                  </div>

                  {listing.transit_stops && listing.transit_stops.length > 0 && (
                    <div className="mb-4">
                      <h3 className="text-sm font-medium text-gray-700 mb-2">Nearby Transit</h3>
                      <div className="flex flex-wrap gap-2">
                        {listing.transit_stops.map((stop, index) => (
                          <div key={index} className="flex items-center bg-gray-100 rounded-full px-3 py-1 text-sm">
                            <Train className="w-4 h-4 mr-1" />
                            <span>{stop.lines.join(', ')} • {stop.walk_minutes}min</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="space-y-4">
                    <ListingInterestButton
                      listingId={listing.id}
                      onShowInterested={() => setSelectedListingForInterest(listing.id)}
                    />
                    
                    <a 
                      href={listing.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="block w-full bg-indigo-600 text-white px-4 py-2 rounded-full hover:bg-indigo-700 transition-colors text-center text-sm md:text-base"
                    >
                      View Details
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {selectedListingForInterest && (
          <InterestedUsersModal
            listingId={selectedListingForInterest}
            onClose={() => setSelectedListingForInterest(null)}
          />
        )}
      </div>
    </div>
  );
}